import { FormApi } from 'final-form';
import { useRef, useLayoutEffect, useCallback } from 'react';
import {
  RevShareFields,
  RevShareFormValues,
  RevShareSettingOption,
} from '../../../data/rev-share/helpers/revShareSettingsUtils';

// Preserves form field values when switching between 'Set fee share' and 'No fee share'
export const useRevShareSettingSwitch = ({
  form,
  revShareSetting,
}: {
  form: FormApi<RevShareFormValues>;
  revShareSetting: RevShareSettingOption | undefined;
}) => {
  const prevRevShareSettingRef = useRef<RevShareSettingOption | undefined>(revShareSetting);
  const customFieldsRef = useRef<RevShareFields | null>(null);

  const reset = useCallback(() => {
    customFieldsRef.current = null;
    prevRevShareSettingRef.current = form.getState().values?.revShareSetting;
  }, [form]);

  const getCurrentFields = useCallback((): RevShareFields => {
    const values = form.getState().values;

    return {
      revShareMethod: values?.revShareMethod,
      baseAmount: values?.baseAmount,
      revSharePct: values?.revSharePct,
      fixedAmount: values?.fixedAmount,
      effectiveTimestamp: values?.effectiveTimestamp,
      expireTimestamp: values?.expireTimestamp,
      setEndDate: values?.setEndDate,
    };
  }, [form]);

  const applyFields = useCallback(
    (fields: RevShareFields) => {
      form.batch(() => {
        form.change('revShareMethod', fields.revShareMethod);
        form.change('baseAmount', fields.baseAmount);
        form.change('revSharePct', fields.revSharePct);
        form.change('fixedAmount', fields.fixedAmount);
        form.change('effectiveTimestamp', fields.effectiveTimestamp);
        form.change('expireTimestamp', fields.expireTimestamp);
        form.change('setEndDate', fields.setEndDate);
      });
    },
    [form],
  );

  const clearEditableFields = useCallback(() => {
    form.batch(() => {
      form.change('revShareMethod', undefined);
      form.change('baseAmount', undefined);
      form.change('revSharePct', undefined);
      form.change('fixedAmount', undefined);
    });
  }, [form]);

  // useLayoutEffect to avoid flashing values when switching settings
  useLayoutEffect(() => {
    const nextSetting = revShareSetting;
    const prevSetting = prevRevShareSettingRef.current;

    if (prevSetting === nextSetting) {
      return;
    }

    if (!prevSetting) {
      prevRevShareSettingRef.current = nextSetting;
      return;
    }

    // Switching from 'Set fee share' → 'No fee share': save fields, then clear
    if (prevSetting === 'Set fee share' && nextSetting === 'No fee share') {
      customFieldsRef.current = getCurrentFields();
      clearEditableFields();
    }

    // Switching from 'No fee share' → 'Set fee share': restore saved fields if any
    if (prevSetting === 'No fee share' && nextSetting === 'Set fee share') {
      if (customFieldsRef.current?.revShareMethod) {
        applyFields(customFieldsRef.current);
      } else {
        clearEditableFields();
      }
    }

    prevRevShareSettingRef.current = nextSetting;
  }, [revShareSetting, getCurrentFields, applyFields, clearEditableFields]);

  return { reset };
};
